import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import get from "lodash/get";
import { Spin, Tooltip, Typography, Button } from "antd";
import format from "date-fns/format";
import moment from "moment/min/moment-with-locales";
import { enUS, es } from "date-fns/locale";

import { DateSearchTypeEnum, ICondo, IGroupedShareCompareCondo, IShareCompareCondo, ISessionKey, SearchTypeEnum, StayForEnum } from "@share/common-types";
import { RefundableEnum } from "@common-types";
import {
  IMarginatorState,
  ILoginState,
  condoStrictDatesActions,
  condoFlexibleDatePickerActions,
  condoGuestsActions,
  condosLocationPickerActions,
  condosActions,
  getCondos,
} from "@share/store/slices";
import { formatDateCheckInOut, renderNumber, isSpanish, getSelectedCurrency, LoginType, UrlUtils, getAccountNumberPath } from "@share/utils";
import { HasPermission, ViewQuotesSharePermission, } from "@utils";
import {
  CONDO_DATES_FLEXIBLE_LABEL,
  CONDO_DATES_LABEL,
  CONDO_GUESTS_LABEL,
  CONDO_IS_FLEXIBLE_LABEL,
  CONDO_LOCATIONS_LABEL,
  CONDO_LOCATIONS_OBJECT_LABEL,
  CONDO_SESSION_KEY,
  DEFAULT_PAGE_NUMBER,
  DATE_FORMAT_CHECK_IN_OUT,
} from "@constants";
import { Routes } from "@share/constants";
import { Currency } from "@components";
import { ModalConfirmation } from "@components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { BlueButton } from "@share/components";
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";

interface ICondoTabProps {
  condos: Record<string, IGroupedShareCompareCondo[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  condosessionKey: ISessionKey | null | undefined;
  onCondoRemove: (condo: IShareCompareCondo) => void;
  onCondoGroupRemove: (condos: IShareCompareCondo[]) => void;
  onCancel: () => void;
  handelDownload: () => void;
  handelShare: () => void;
}

const percents = 100;
const DATE_FORMAT = "d MMM";
const ONE = 1;

export const CondoTab: React.FC<ICondoTabProps> = ({
  condos,
  marginatorStore,
  loginStore,
  condosessionKey,
  onCondoRemove,
  onCondoGroupRemove,
  onCancel,
  handelDownload,
  handelShare,
}) => {
  const locations = Object.keys(condos);
  const [selectedLocation, setSelectedLocation] = useState(locations[0] || 'unknown_location');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const history = useHistory();
  const dispatch = useDispatch();
  const { account, user } = loginStore;
  const currency = getSelectedCurrency(account as any);
  const [loading, setLoading] = useState(false);
  const [showRedirectConfirmation, setShowRedirectConfirmation] = useState(false);
  const [pendingGroup, setPendingGroup] = useState<IGroupedShareCompareCondo | null>(null);

  const currentGroups = condos[selectedLocation] || [];

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  useEffect(() => {
    if (!selectedLocation || !condos[selectedLocation]) {
      const firstLoc = Object.keys(condos)[0];
      if (firstLoc) setSelectedLocation(firstLoc);
    }
  }, [condos]);

  const locationName = (currentGroups[activeGroupIndex]?.items?.[0] as any)?.locationObj?.name;

  const handleView = (condo: ICondo) => {
    const { condoId } = condo;
    const selectedGroup = currentGroups[activeGroupIndex];
    if (!selectedGroup) return;
    
    const firstCondo = selectedGroup.items?.[0] as any;
    const locationObj = firstCondo?.locationObj;
    const locationLabel = locationObj?.name || "";
    const searchType = (selectedGroup.searchType || DateSearchTypeEnum.Strict) as DateSearchTypeEnum;
    const isFlexible = searchType === DateSearchTypeEnum.Flexible;
    const startDate = selectedGroup.startDate && selectedGroup.startDate !== "unknown" ? selectedGroup.startDate : undefined;
    const endDate = selectedGroup.endDate && selectedGroup.endDate !== "unknown" ? selectedGroup.endDate : undefined;
    const selectedMonths = selectedGroup.selectedMonths || [];
    const adultsCount = selectedGroup.adultsCount ?? 2;
    const kidsCount = selectedGroup.kidsCount ?? 0;
    const bedroomsCount = selectedGroup.bedroomsCount ?? 1;
    const includeStudio = firstCondo?.includeStudio ?? true;
    const sessionKey = firstCondo?.sessionKey;

    const params = [
      { key: CONDO_LOCATIONS_OBJECT_LABEL, value: locationObj },
      { key: CONDO_LOCATIONS_LABEL, value: locationLabel },
      { key: CONDO_IS_FLEXIBLE_LABEL, value: isFlexible ? "1" : "0" },
      { key: CONDO_DATES_LABEL, value: isFlexible ? null : { startDate, endDate } },
      { key: CONDO_DATES_FLEXIBLE_LABEL, value: isFlexible ? { selectedMonths, stayFor: StayForEnum.Week } : null },
      {
        key: CONDO_GUESTS_LABEL,
        value: {
          adultsCount,
          kidsCount,
          bedroomsCount,
          includeStudio,
        },
      },
      { key: CONDO_SESSION_KEY, value: sessionKey },
    ];

    const queryString = UrlUtils.getQueryString(params);
    const accountPath = getAccountNumberPath(history);
    const Url = `${accountPath}${Routes.Condo}/${condoId}?${queryString}`;
    window.open(Url, "_blank");
  };

  const onCondoSearchPage = (condoGroup: IGroupedShareCompareCondo) => {
    if (!condoGroup) return;

    const firstCondo = condoGroup.items?.[0] as any;
    const locationObj = firstCondo?.locationObj;
    const locationLabel = locationObj?.name || "";

    const searchType = (condoGroup.searchType || DateSearchTypeEnum.Strict) as DateSearchTypeEnum;
    const isFlexible = searchType === DateSearchTypeEnum.Flexible;
    const startDate = condoGroup.startDate && condoGroup.startDate !== "unknown" ? condoGroup.startDate : undefined;
    const endDate = condoGroup.endDate && condoGroup.endDate !== "unknown" ? condoGroup.endDate : undefined;
    const selectedMonths = condoGroup.selectedMonths || [];

    const adultsCount = condoGroup.adultsCount ?? 2;
    const kidsCount = condoGroup.kidsCount ?? 0;
    const bedroomsCount = condoGroup.bedroomsCount ?? 1;
    const includeStudio = firstCondo?.includeStudio ?? true;

    if (locationObj) {
      UrlUtils.setUrl(CONDO_LOCATIONS_OBJECT_LABEL, locationObj);
      UrlUtils.setUrl(CONDO_LOCATIONS_LABEL, locationLabel);
      dispatch(condosLocationPickerActions.selectLocation(locationObj));
      dispatch(condosLocationPickerActions.selectLocationLabel(locationLabel));
    }

    UrlUtils.setUrl(CONDO_IS_FLEXIBLE_LABEL, isFlexible ? "1" : "0");
    if (isFlexible) {
      UrlUtils.setUrl(CONDO_DATES_LABEL, null);
      UrlUtils.setUrl(CONDO_DATES_FLEXIBLE_LABEL, { selectedMonths, stayFor: StayForEnum.Week });
    } else {
      UrlUtils.setUrl(CONDO_DATES_FLEXIBLE_LABEL, null);
      UrlUtils.setUrl(CONDO_DATES_LABEL, { startDate, endDate });
    }
    UrlUtils.setUrl(CONDO_GUESTS_LABEL, { adultsCount, kidsCount, bedroomsCount, includeStudio });
    if (condosessionKey) {
      UrlUtils.setUrl(CONDO_SESSION_KEY, condosessionKey);
    }

    dispatch(condoStrictDatesActions.setSearchType(searchType));
    dispatch(condoStrictDatesActions.setDates({ startDate, endDate }));
    dispatch(condoFlexibleDatePickerActions.setState({ stayFor: StayForEnum.Week, selectedMonths: isFlexible ? selectedMonths : [] }));
    dispatch(condoGuestsActions.setState({
      adultsCount,
      kidsCount,
      bedroomsCount,
      includeStudio,
      prevAdultsCount: adultsCount,
      prevKidsCount: kidsCount,
      prevBedroomsCount: bedroomsCount,
      prevIncludeStudio: includeStudio,
    }));

    dispatch(condosActions.setPageNumber(DEFAULT_PAGE_NUMBER));
    dispatch(condosActions.setIsSearch(true));
    dispatch(getCondos(SearchTypeEnum.NewSearch));

    onCancel?.();

    const currentPath = history.location.pathname.toLowerCase();
    const isCondoPage = currentPath.includes("/condo");
    if (!isCondoPage) {
      const accountName = account?.name || "";
      history.push(`/${accountName}${Routes.CondoSearch}${window.location.search}`);
    }

  };

  const redirectMessage = 'You will redirect to search page with this specific date and detail. Are you sure?';

  const onClickMeta = () => {
    const selectedGroup = currentGroups[activeGroupIndex];
    if (!selectedGroup) return;
    setPendingGroup(selectedGroup);
    setShowRedirectConfirmation(true);
  };

  const onConfirmRedirect = () => {
    if (pendingGroup) {
      onCondoSearchPage(pendingGroup);
    }
    setShowRedirectConfirmation(false);
    setPendingGroup(null);
  };

  const onCancelRedirect = () => {
    setShowRedirectConfirmation(false);
    setPendingGroup(null);
  };

  return (
    <div>
      {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locId) => (
                  <button
                    key={locId}
                    className={`tab-btn ${
                      selectedLocation === locId ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedLocation(locId);
                      setActiveGroupIndex(0);
                    }}
                  >
                    {(condos[locId][0]?.items[0] as any)?.locationObj?.name ||
                      locId}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* DATE TABS */}
          {currentGroups.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => {
                  let label: React.ReactNode = "";

                  if (group.startDate && group.endDate) {
                    label = `${formatDateCheckInOut(
                      group.startDate,
                      DATE_FORMAT_CHECK_IN_OUT,
                      isSpanish() ? es : undefined
                    )} - ${formatDateCheckInOut(
                      group.endDate,
                      DATE_FORMAT_CHECK_IN_OUT,
                      isSpanish() ? es : undefined
                    )}`;
                  } else if (
                    group.searchType === "Flexible" &&
                    group.selectedMonths?.length
                  ) {
                    const formattedMonths = group.selectedMonths.map((month) =>
                      moment(month)
                        .locale(isSpanish() ? "es" : "en")
                        .format("MMMM")
                    );

                    const years = group.selectedMonths.map((m) =>
                      moment(m).format("YYYY")
                    );

                    const allSameYear = years.every((y) => y === years[0]);

                    label = allSameYear
                      ? `${formattedMonths.join(" - ")} ${years[0]}`
                      : group.selectedMonths
                          .map((m) =>
                            moment(m)
                              .locale(isSpanish() ? "es" : "en")
                              .format("MMMM YYYY")
                          )
                          .join(" - ");
                  } else if (group.searchType === "Flexible") {
                    label = "Anytime";
                  }

                  return (
                    <button
                      key={index}
                      className={`tab-btn ${
                        activeGroupIndex === index ? "active" : ""
                      }`}
                      onClick={() => setActiveGroupIndex(index)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='modal-compare__hotels-location'>
        {locationName ? <div>{locationName}</div> : null}
      </div>

      <div className="condo-modal-compare__hotels condo-modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <a className='modal-compare__hotels_link' onClick={onClickMeta}>
          <div className="hotels-dates">
            {currentGroups[activeGroupIndex]?.searchType === "Flexible" ? (
              <>
                <FormattedMessage id="flexible.dates" /> -{" "}
                {currentGroups[activeGroupIndex]?.selectedMonths?.length ? (
                  currentGroups[activeGroupIndex]?.selectedMonths
                    ?.map((m) => moment(m, "DD MMM yyyy").format("MMMM"))
                    .join(", ")
                ) : (
                  <FormattedMessage id="anytime" />
                )}
              </>
            ) : (
              <>
                <FormattedMessage id="specific.dates" /> -{" "}
                {formatDateCheckInOut(
                  currentGroups[activeGroupIndex]?.startDate as string,
                  DATE_FORMAT_CHECK_IN_OUT,
                  isSpanish() ? es : undefined
                )}{" "}
                -{" "}
                {formatDateCheckInOut(
                  currentGroups[activeGroupIndex]?.endDate as string,
                  DATE_FORMAT_CHECK_IN_OUT,
                  isSpanish() ? es : undefined
                )}
              </>
            )}
          </div>
          <div className="hotels-dates text-start">
            <span className="compare-meta-item">
              <FormattedMessage id="adult" values={{ count: currentGroups[activeGroupIndex]?.adultsCount }} />: <strong>{currentGroups[activeGroupIndex]?.adultsCount}</strong>
            </span>
            <span className="compare-meta-item">
              <FormattedMessage id="child" values={{ count: currentGroups[activeGroupIndex]?.kidsCount }} />: <strong>{currentGroups[activeGroupIndex]?.kidsCount}</strong>
            </span>
            <span className="compare-meta-item">
              <FormattedMessage id="bedroom" values={{ count: currentGroups[activeGroupIndex]?.bedroomsCount }} />: <strong>{currentGroups[activeGroupIndex]?.bedroomsCount}</strong>
            </span>
          </div>
        </a>
        {currentGroups.length > 1 && (
          <Button
            danger
            onClick={() => onCondoGroupRemove(currentGroups[activeGroupIndex]?.items)}
          >
            <FormattedMessage id="results.hotel.card.compare.modal.remove_quote" defaultMessage="Remove Quote" />
          </Button>
        )}
      </div>

      <div className="condo-modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((condo, index) => {
          const { price, availableDates } = condo;

          const value = marginatorStore.value || 0;
          const marginatorPrice = (price ? price : 0) / (1 - value / 100);

          const msave = condo.publicPrice
            ? (marginatorPrice * percents) / condo.publicPrice
            : 100;
          const savings = Math.floor(percents - msave);

          return (
            <div key={index} className="condo-modal-compare__hotel">
              <div className="condo-modal-compare__media">
                <div className="condo-images-container">
                  <div
                    className="condo-image"
                    style={{
                      backgroundImage: `url(${get(condo, "images[0].url")})`,
                    }}
                  />
                  {savings > 0 ? (
                    <div className="condo-modal-compare__savings-badge">
                      {savings.toFixed(0)} %{" "}
                      <FormattedMessage id="results.hotel.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
                <label
                  className={`condo-modal-compare__remove-btn ${
                    currentGroups[activeGroupIndex]?.items.length <= 2
                      ? "disabled"
                      : ""
                  }`}
                  onClick={() => {
                    if (currentGroups[activeGroupIndex]?.items.length > 2) {
                      onCondoRemove(condo);
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCircleXmark}
                    className="condo-modal-compare__remove-btn-icon"
                  />
                </label>
              </div>

              <div className="condo-text-container condo-text-container--title">
                <Tooltip title={condo.displayName} placement="top">
                  <p>{condo.displayName}</p>
                </Tooltip>
              </div>

              <div className="condo-text-container condo-text-container--address">
                <Tooltip title={condo.fullAddress ?? ""} placement="top">
                  <p>{condo.fullAddress}</p>
                </Tooltip>
              </div>

              <div
                className="condo-text-container"
                style={{ height: isSpanish() ? "60px" : "40px" }}
              >
                {availableDates?.length ? (
                  <div className="condo-modal-compare__date-label">
                    {availableDates.length >= ONE && (
                      <div className="condo-modal-compare__date-more">
                        <p className="condo-modal-compare__date-more-text">
                          <Tooltip
                            className="condo-compare-tooltip"
                            overlayStyle={{ whiteSpace: "pre-line" }}
                            placement="top"
                            title={
                              <>
                                {availableDates.map((d, i) => (
                                  <Typography
                                    key={i}
                                    style={{ color: "#FFFFFF" }}
                                  >
                                    {format(new Date(d.from), DATE_FORMAT, {
                                      locale: isSpanish() ? es : enUS,
                                    })}{" "}
                                    -{" "}
                                    {format(new Date(d.to), DATE_FORMAT, {
                                      locale: isSpanish() ? es : enUS,
                                    })}
                                  </Typography>
                                ))}
                              </>
                            }
                          >
                            {availableDates.length}{" "}
                            <FormattedMessage id="available.dates" />
                          </Tooltip>
                        </p>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {condo.refundability &&
              condo.refundability !== RefundableEnum.Unknown ? (
                <div className="condo-text-container condo-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {condo.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {condo.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div
                className="condo-amenities-container"
                style={{ height: isSpanish() ? "60px" : "43px" }}
              >
                {condo.rating?.value || condo.rating?.reviewsCount ? (
                  <label>
                    {condo.rating?.value ? (
                      <>
                        <strong>{condo.rating?.value.toFixed(1)}</strong> / 5
                      </>
                    ) : null}{" "}
                    {condo.rating?.reviewsCount ? (
                      <>
                        ({condo.rating?.reviewsCount}{" "}
                        <FormattedMessage id="reviews" />)
                      </>
                    ) : null}
                  </label>
                ) : (
                  <label>
                    <FormattedMessage id="reviews.guest.no.ratings" />
                  </label>
                )}
              </div>

              <div className="condo-text-container condo-text-container--price">
                {condo.publicPrice ? (
                  <p style={{ textDecoration: "line-through" }}>
                    <Currency currency={currency} />
                    {renderNumber(condo.publicPrice, 2)}
                  </p>
                ) : null}
                <p>
                  <strong>
                    <Currency currency={currency} />
                    {renderNumber(marginatorPrice, 2)}
                  </strong>
                </p>
              </div>

              <div className="condo-view-container">
                <div className="view-button" onClick={() => handleView(condo)}>
                  <FormattedMessage id="results.hotel.card.compare.modal.view" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="condo-modal-compare__buttons">
        {!account || account.type !== LoginType.PrivateWithToken ? (
          account?.displayShareCompareCondos && (
            <BlueButton disabled={loading} onClick={handelDownload}>
              {loading ? (
                <Spin />
              ) : (
                <FormattedMessage id="results.hotel.card.compare.modal.download" />
              )}
            </BlueButton>
          )
        ) : (
          <>
            {account?.displayShareCompareCondos && (
              <BlueButton
                disabled={loading}
                onClick={handelDownload}
              >
                {loading ? (
                  <Spin />
                ) : (
                  <FormattedMessage id="results.hotel.card.compare.modal.download" />
                )}
              </BlueButton>
            )}

            {HasPermission(user, ViewQuotesSharePermission) ? (
              <BlueButton
                disabled={loading}
                onClick={handelShare}
              >
                {loading ? (
                  <Spin />
                ) : (
                  <FormattedMessage id="results.hotel.card.compare.modal.share" />
                )}
              </BlueButton>
            ) : null}
          </>
        )}
      </div>
      <ModalConfirmation
        className="modal-share"
        wrapClassName="modal-share__confirmation-wrapper"
        title="results.hotel.card.compare.modal.share"
        message={redirectMessage}
        yesMessage="share.continue"
        noMessage="share.cancel"
        visible={showRedirectConfirmation}
        disableMessageLocalization
        onCancel={onCancelRedirect}
        onConfirm={onConfirmRedirect}
      />
    </div>
  );
};
